// src/components/personal/sections/OrdersSection/components/OrderInfo/OrderPrintContent.tsx
import { forwardRef } from 'react';
import { Order } from '../../types/types';
import { formatPhoneNumber } from '@/utils/Formatters';
import { formatDateTime } from '@/utils/dateUtils';
import { getOrderStatusTextStatic, getSalesChannelTextStatic, getPaymentStatusTextStatic } from '../../utils/statusHelpers';
import { TFunction } from 'i18next';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface OrderPrintContentProps {
  order: Order;
  t: TFunction;
  i18n: {
    language: string;
  };
}

// Используем forwardRef для передачи ссылки на этот компонент из родителя
const OrderPrintContent = forwardRef<HTMLDivElement, OrderPrintContentProps>(
  ({ order, t, i18n }, ref) => {
    const { formatPrice } = useBusinessCurrency();
    
    // Безопасное извлечение данных cost_data
    const subtotal = order.cost_data?.subtotal || 0;
    const shippingFee = order.cost_data?.shipping_fee || 0;
    const total = order.cost_data?.total || 0;
    
    // Вычисление суммы всех скидок
    const getDiscountsTotal = () => {
      if (!order.cost_data?.discounts) return 0;
      
      return Object.entries(order.cost_data.discounts).reduce((sum, [_, value]) => {
        // Проверяем, что значение - это число
        const numValue = typeof value === 'number' ? value : parseFloat(value || '0');
        return sum + (isNaN(numValue) ? 0 : numValue);
      }, 0);
    };
    
    const totalDiscount = getDiscountsTotal();
    
    // Извлекаем адрес доставки и комментарий
    const getDeliveryAddress = () => {
      if (!order.user_data) return null;
      
      const addressParts = [];
      if (order.user_data.street) addressParts.push(order.user_data.street);
      if (order.user_data.house) addressParts.push(`дом ${order.user_data.house}`);
      if (order.user_data.apartment) addressParts.push(`кв. ${order.user_data.apartment}`);
      
      return addressParts.length > 0 ? addressParts.join(', ') : null;
    };
    
    const deliveryAddress = getDeliveryAddress() || order.deliveryAddress;
    const deliveryComment = order.user_data?.comment || order.deliveryComment;

    // Добавляем безопасный доступ к order_data и payments
    const orderItems = order.order_data || [];
    const payments = order.payments || [];

    return (
      <div ref={ref} className="p-6">
        <h2 className="text-xl font-bold text-center mb-4">
          {t('order_details.order_number', { id: order.id })}
        </h2>
        
        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <span>{t('table.date')}:</span>
            <span>{formatDateTime(order.created_at, true, i18n.language)}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span>{t('table.client')}:</span>
            <span>{order.client_name || t('table.unknown_client')}</span>
          </div>
          {order.client_phone && (
            <div className="flex justify-between mb-2">
              <span>{t('table.phone')}:</span>
              <span>{formatPhoneNumber(order.client_phone)}</span>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <span>{t('table.platform')}:</span>
            <span>{getSalesChannelTextStatic(order.platform, t)}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span>{t('table.status')}:</span>
            <span>{getOrderStatusTextStatic(order.status, t)}</span>
          </div>
        </div>
        
        {/* Добавляем раздел с данными доставки */}
        {(deliveryAddress || deliveryComment) && (
          <>
            <hr className="my-4" />
            <h3 className="text-lg font-semibold mb-2">{t('order_details.delivery_info')}</h3>
            
            {deliveryAddress && (
              <div className="mb-2">
                <strong>{t('order_details.delivery_address')}:</strong> {deliveryAddress}
              </div>
            )}
            
            {deliveryComment && (
              <div className="mb-4">
                <strong>{t('order_details.comment')}:</strong> {deliveryComment}
              </div>
            )}
          </>
        )}
        
        <hr className="my-4" />
        
        <h3 className="text-lg font-semibold mb-2">{t('order_details.tabs.items')}</h3>
        <table className="w-full mb-4">
          <thead>
            <tr>
              <th className="text-left">{t('table.name')}</th>
              <th className="text-center">{t('table.quantity')}</th>
              <th className="text-right">{t('table.price')}</th>
              <th className="text-right">{t('table.total')}</th>
            </tr>
          </thead>
          <tbody>
            {orderItems.map((item, index) => (
              <tr key={item.id || index}>
                <td>{item.name}</td>
                <td className="text-center">{item.quantity}</td>
                <td className="text-right">{item.price}</td>
                <td className="text-right">{item.total}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Детализированный расчет стоимости */}
            <tr>
              <td colSpan={3} className="text-right pt-2 border-t">
                {t('order_details.subtotal')}:
              </td>
              <td className="text-right pt-2 border-t">
                {formatPrice(subtotal)}
              </td>
            </tr>
            
            {totalDiscount > 0 && (
              <tr>
                <td colSpan={3} className="text-right text-green-600">
                  {t('order_details.discounts')}:
                </td>
                <td className="text-right text-green-600">
                  -{formatPrice(totalDiscount)}
                </td>
              </tr>
            )}
            
            {shippingFee > 0 && (
              <tr>
                <td colSpan={3} className="text-right">
                  {t('order_details.shipping_fee')}:
                </td>
                <td className="text-right">
                  {formatPrice(shippingFee)}
                </td>
              </tr>
            )}
            
            <tr>
              <td colSpan={3} className="text-right font-bold pt-2">
                {t('table.grand_total')}:
              </td>
              <td className="text-right font-bold pt-2">
                {formatPrice(total)}
              </td>
            </tr>
          </tfoot>
        </table>
        
        <hr className="my-4" />
        
        <h3 className="text-lg font-semibold mb-2">{t('order_details.tabs.payments')}</h3>
        {payments.length > 0 ? (
          <table className="w-full mb-4">
            <thead>
              <tr>
                <th className="text-left">{t('payment.amount')}</th>
                <th className="text-left">{t('payment.method')}</th>
                <th className="text-left">{t('payment.status')}</th>
                <th className="text-right">{t('table.date')}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatPrice(payment.amount)}</td>
                  <td>{t(`payment_method.${payment.method}`)}</td>
                  <td>{getPaymentStatusTextStatic(payment.status, t)}</td>
                  <td className="text-right">
                    {payment.payment_date 
                      ? formatDateTime(payment.payment_date, true, i18n.language)
                      : formatDateTime(payment.created_at, true, i18n.language)
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center text-gray-500">{t('order_details.no_payments')}</p>
        )}
        
        <div className="text-center text-sm mt-8">
          {t('receipt.thank_you')}
        </div>
      </div>
    );
  }
);

export default OrderPrintContent;